import React from 'react';
import Badge from '@material-ui/core/Badge';
import { css } from '@emotion/core';
import { promo500, system100 } from 'Mui/styles/color';
import { Tooltip } from '@material-ui/core';
import { adjustColorAlpha } from 'utils/colorConvert';

const BUTTON_VARIANT = {
    CONTAINED: 'contained',
    OUTLINED: 'outlined'
};

/**
 * @method FeverBadgeButton - 帶有 Icon 與數量 的按鈕
 * @param {Object}  props
 * @param {String}  props.Icon                - 圖示 ( 優先級大於圖片 URL )
 * @param {String}  props.imageUrl            - 按鈕圖示的圖片連結
 * @param {Number}  props.size                - 按鈕大小 ( 單位：px )
 * @param {Number}  props.number              - 按鈕訊息數
 * @param {Number}  props.maxNumber           - 按鈕訊息數最大值 （ Ex. 設定 999 ，超過則會顯示 999+ )
 * @param {Object}  props.numberStyle         - 按鈕訊息數樣式
 * @param {String}  props.numberStyle.color   - 按鈕訊息數文字顏色
 * @param {String}  props.numberStyle.bgColor - 按鈕訊息數背景顏色
 * @param {Object}  props.buttonSyle          - 按鈕顏色樣式
 * @param {String}  props.buttonSyle.color    - 按鈕文字顏色
 * @param {String}  props.buttonSyle.icon     - 按鈕圖示顏色
 * @param {String}  props.buttonSyle.bgColor  - 按鈕背景顏色
 * @param {Boolean} props.showNumber          - 是否顯示訊息數
 * @param {Boolean} props.showZero            - 是否顯示 0
 * @param {String}  props.variant             - 按鈕模式 ( contained - 填滿 , outlined - 框線 )
 * @param {String}  props.toolTip             - 按鈕 hover tooltip
 * @param {Boolean} props.disabled            - 禁用
 * @param {String}  props.onClick             - 點擊事件
 * @returns {JSX.Element}
 */

export default function FeverBadgeButton({
    Icon,
    imageUrl = '',
    size = 48,
    number = 0,
    maxNumber = 999,
    numberStyle = {
        color: '#fff',
        bgColor: promo500.color
    },
    buttonSyle = {
        background: adjustColorAlpha(promo500.color, 0.16),
        hoverBackground: adjustColorAlpha(promo500.color, 0.3),
        icon: promo500.color,
        border: ''
    },
    showNumber = true,
    showZero = false,
    variant = BUTTON_VARIANT.CONTAINED,
    toolTip = '',
    disabled = false,
    onClick = () => {}
}) {
    return (
        <div
            css={css(containerStyle(disabled))}
            onClick={() => {
                if (!disabled) onClick();
            }}
        >
            <Tooltip title={toolTip}>
                <Badge
                    css={css(numberContainerStyle(numberStyle))}
                    {...(showNumber && {
                        overlap: 'circular',
                        badgeContent: Number(number),
                        max: maxNumber,
                        showZero
                    })}
                >
                    <div css={css(iconContainerStyle({ size, variant, ...buttonSyle, disabled }))}>
                        {Icon && <Icon css={css(iconStyle({ size, ...buttonSyle }))} />}
                        {!Icon && imageUrl && (
                            <img
                                src={imageUrl}
                                css={css(iconStyle({ size }))}
                                alt="FeverBadgeButton"
                            />
                        )}
                    </div>
                </Badge>
            </Tooltip>
        </div>
    );
}

const iconStyle = ({ size, icon = promo500.color }) => css`
    color: ${icon};
    width: ${size / 2}px;
    height: ${size / 2}px;
`;

const iconContainerStyle = ({
    size,
    background = adjustColorAlpha(promo500.color, 0.16),
    border,
    hoverBackground = adjustColorAlpha(promo500.color, 0.3),
    variant,
    disabled
}) => css`
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${size}px;
    height: ${size}px;
    ${variant === BUTTON_VARIANT.CONTAINED && disabled
        ? `background-color: ${system100.color}`
        : `background-color: ${background}`};
    ${variant === BUTTON_VARIANT.CONTAINED &&
    !disabled &&
    `&:hover {background-color: ${
        Boolean(hoverBackground) ? hoverBackground : adjustColorAlpha(background, 0.5)
    }}`};
    ${variant === BUTTON_VARIANT.OUTLINED && `border: solid 2px ${border};`}
`;

const containerStyle = disabled => css`
    ${disabled ? 'cursor:not-allowed;' : 'cursor:pointer;'}
    & > * {
        margin: 8px;
    }
`;

const numberContainerStyle = ({ color = '#fff', bgColor = promo500.color } = {}) => css`
    .MuiBadge-badge {
        color: ${color};
        background-color: ${bgColor};
        line-height: unset;
        letter-spacing: normal;
    }
`;
